import React, { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AgentEvents,
  BriefingConfigUpdatedEvent,
  BriefingRoutineEntry,
  GetBriefingConfigResponse,
  GetDailyStandupSettingsResponse,
} from '@dreamai/types';
import { usePusher } from '../../contexts/PusherContext';
import { useReplyToActions } from '../../contexts/ReplyToContext';
import api from '../../api';
import { ReactComponent as SunSettingIcon } from '../../icons/sun-setting.svg';
import { CategoryIcon } from './RoutinesSection';
import './daily-briefing-drawer.scss';

interface DailyBriefingDrawerProps {
  open: boolean;
  onClose: () => void;
  onViewRoutine?: (autopilotId: string, routineName: string) => void;
}

const DAILY_BRIEFING_REPLY_TO_ID = 'daily-briefing-edit';

const DailyBriefingDrawer: React.FC<DailyBriefingDrawerProps> = ({
  open,
  onClose,
  onViewRoutine,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { addReplyTo, removeReplyTo } = useReplyToActions();
  const [rules, setRules] = useState<string[]>([]);
  const [routines, setRoutines] = useState<BriefingRoutineEntry[]>([]);
  const [callTime, setCallTime] = useState('07:30');
  const [loading, setLoading] = useState(true);

  const fetchBriefingConfig = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${token}` };

      const [configRes, settingsRes] = await Promise.all([
        api.get<GetBriefingConfigResponse>('/daily-standup/briefing-config', {
          headers,
        }),
        api.get<GetDailyStandupSettingsResponse>('/daily-standup/settings', {
          headers,
        }),
      ]);

      if (configRes.data.success) {
        setRules(configRes.data.rules || []);
        setRoutines(configRes.data.routines || []);
      }
      if (settingsRes.data.success && settingsRes.data.settings) {
        setCallTime(settingsRes.data.settings.callTime.slice(0, 5));
      }
    } catch (err) {
      console.error('Error fetching briefing config:', err);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchBriefingConfig();
  }, [fetchBriefingConfig]);

  // Show the glow reply-to pill above the chat input while the drawer is open
  useEffect(() => {
    if (!open) return;

    addReplyTo({
      id: DAILY_BRIEFING_REPLY_TO_ID,
      actionLine: "Tell me how you'd like to change your briefing",
      descriptionLine: '',
      extraContext:
        'The user is viewing their Daily Briefing settings and is discussing with you changes to their daily briefing (rules, routines, schedule)',
      persistent: true,
      glow: true,
    });

    return () => {
      removeReplyTo(DAILY_BRIEFING_REPLY_TO_ID);
    };
  }, [open, addReplyTo, removeReplyTo]);

  // Close on Escape while open
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // Listen for real-time updates via Pusher
  const pusherContext = usePusher(true);
  const userChannel = (pusherContext as any)?.userChannel;

  useEffect(() => {
    if (!userChannel) return;

    const handleConfigUpdated = (data: BriefingConfigUpdatedEvent) => {
      setRules(data.rules || []);
      setRoutines(data.routines || []);
    };

    userChannel.bind(AgentEvents.BRIEFING_CONFIG_UPDATED, handleConfigUpdated);
    return () => {
      userChannel.unbind(
        AgentEvents.BRIEFING_CONFIG_UPDATED,
        handleConfigUpdated,
      );
    };
  }, [userChannel]);

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return `Started ${date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}`;
    } catch {
      return '';
    }
  };

  return (
    <div
      className={`daily-briefing-drawer${open ? ' open' : ''}`}
      aria-hidden={!open}
    >
      <div className='daily-briefing-drawer-inner'>
        <header className='daily-briefing-drawer-header'>
          <div className='daily-briefing-drawer-header-title-block'>
            <SunSettingIcon className='daily-briefing-drawer-header-icon' />
            <h1 className='daily-briefing-drawer-title'>Daily Briefing</h1>
          </div>
          <div className='daily-briefing-drawer-intro'>
            <p>
              At <strong>{callTime} every day</strong> I'll send your Daily
              Briefing by <strong>email</strong>, and a summary on{' '}
              <strong>WhatsApp</strong>.
            </p>
            <p>
              Tell me what you want to include, and I'll setup the Routines you
              need for it.
            </p>
          </div>
        </header>

        {loading ? (
          <div className='daily-briefing-drawer-loading'>Loading...</div>
        ) : (
          <>
            <section className='daily-briefing-drawer-section'>
              <h2 className='daily-briefing-drawer-section-title'>Rules</h2>
              <p className='daily-briefing-drawer-section-subtitle'>
                How your briefing is laid out. For example "Always include my
                calendar summary at the top of my email, but never show it on
                WhatsApp"
              </p>
              {rules.length > 0 ? (
                <div className='daily-briefing-drawer-rules'>
                  {rules.map((rule, i) => (
                    <p key={i} className='daily-briefing-drawer-rule'>
                      {rule}
                    </p>
                  ))}
                </div>
              ) : (
                <p className='daily-briefing-drawer-empty'>
                  No rules set yet. Ask me to customise your briefing layout.
                </p>
              )}
            </section>

            <section className='daily-briefing-drawer-section'>
              <h2 className='daily-briefing-drawer-section-title'>Routines</h2>
              {routines.length > 0 ? (
                <div className='daily-briefing-drawer-routines'>
                  {routines.map((routine) => (
                    <div
                      key={routine.autopilotId}
                      className='daily-briefing-drawer-routine'
                      onClick={() =>
                        onViewRoutine?.(routine.autopilotId, routine.name)
                      }
                      role='button'
                      tabIndex={0}
                    >
                      <div className='daily-briefing-drawer-routine-icon'>
                        <CategoryIcon
                          category={routine.categories?.[0] || 'other'}
                        />
                      </div>
                      <div className='daily-briefing-drawer-routine-content'>
                        <span className='daily-briefing-drawer-routine-name'>
                          {routine.name}
                        </span>
                        <span className='daily-briefing-drawer-routine-summary'>
                          {routine.summary}
                        </span>
                      </div>
                      <span className='daily-briefing-drawer-routine-date'>
                        {formatDate(routine.addedAt)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className='daily-briefing-drawer-empty'>
                  No information being included in your daily briefing yet.
                </p>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default DailyBriefingDrawer;
